import { Loader2, Check, Sparkles, Brain, Search, FileText, MessageSquare } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";

interface GenerationProgressProps {
  currentStep: string;
  message: string;
  boardDetails?: { name: string; description: string } | null;
  personas?: any[];
}

const STEPS = [
  { key: 'gatherContext', label: 'Analysing Context', icon: Search },
  { key: 'generateBoardDetails', label: 'Drafting Board Details', icon: FileText },
  { key: 'evaluateBoardDetails', label: 'Evaluating Details', icon: Check },
  { key: 'generateBasicPersonas', label: 'Identifying Roles', icon: Brain },
  { key: 'enrichPersonas', label: 'Fleshing Out Details', icon: Sparkles },
  { key: 'evaluatePersonas', label: 'Reviewing Personas', icon: Check },
  { key: 'finalizing', label: 'Finalising', icon: Sparkles },
];

export const GenerationProgress = ({ currentStep, message, boardDetails, personas }: GenerationProgressProps) => {
  const currentStepIndex = STEPS.findIndex(step => step.key === currentStep);

  return (
    <div className="space-y-6">
      <Card className="w-full max-w-md mx-auto">
        <CardHeader className="text-center">
          <div className="mx-auto w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
            <Sparkles className="h-8 w-8 text-primary animate-pulse" />
          </div>
          <CardTitle>Generating Your Board</CardTitle>
          <p className="text-muted-foreground text-sm">{message}</p>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {STEPS.map((step, index) => {
              const isCompleted = index < currentStepIndex;
              const isCurrent = index === currentStepIndex;

              return (
                <div key={step.key} className="flex items-center gap-3">
                  <div
                    className={cn(
                      "flex h-8 w-8 items-center justify-center rounded-full transition-colors",
                      isCompleted ? "bg-primary text-primary-foreground" :
                      isCurrent ? "bg-primary/20 text-primary" :
                      "bg-muted text-muted-foreground"
                    )}
                  >
                    {isCompleted ? (
                      <Check className="h-5 w-5" />
                    ) : isCurrent ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      <step.icon className="h-5 w-5" />
                    )}
                  </div>
                  <span
                    className={cn(
                      "text-sm font-medium transition-colors",
                      isCompleted ? "text-foreground" : "text-muted-foreground",
                      isCurrent && "text-primary"
                    )}
                  >
                    {step.label}
                  </span>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Progressive Disclosure Preview */}
      {(boardDetails || (personas && personas.length > 0)) && (
        <div className="max-w-md mx-auto space-y-4 animate-fade-in">
          {boardDetails && (
            <Card className="bg-muted/30 border-dashed">
              <CardContent className="p-4 space-y-2">
                <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wider">Drafting Board</h3>
                <p className="font-medium">{boardDetails.name}</p>
                <p className="text-sm text-muted-foreground line-clamp-2">{boardDetails.description}</p>
              </CardContent>
            </Card>
          )}
          
          {personas && personas.length > 0 && (
            <Card className="bg-muted/30 border-dashed">
              <CardContent className="p-4 space-y-2">
                <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wider">Identified Roles</h3>
                <div className="space-y-2">
                  {personas.map((p, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      <div className="h-2 w-2 rounded-full bg-primary/50" />
                      <span className="font-medium">{p.role_name}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};
